/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDynamicDestinationData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for fetching Dynamic Destination data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { fetchDynamicDestinationById } from '../api/dynamicDestinationsApi';
import { FormState, DynamicDestinationOptionFormData } from '../types';
import { getDefaultFormState, createEmptyOption } from '../utils/formDefaults';
import { mapApiOptionToForm } from '../utils/optionHelpers';

/**
 * Hook for fetching Dynamic Destination data
 */
export function useDynamicDestinationData(id: string | undefined, duplicateId: string | null) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormState | null>(null);
  const { showError } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const tRef = useRef(t);

  useEffect(() => {
    showErrorRef.current = showError;
    tRef.current = t;
  }, [showError, t]);

  const loadDynamicDestination = useCallback(
    async (entryId: string, duplicate: boolean = false) => {
      setIsLoading(true);
      try {
        const entry = await fetchDynamicDestinationById(entryId);
        
        const adaptedOptions: DynamicDestinationOptionFormData[] = Array.isArray(entry.options)
          ? entry.options
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((option: any, index: number) => mapApiOptionToForm(option, index))
              .sort((a, b) => a.priority - b.priority)
              .map((option, index) => ({ ...option, priority: index + 1 }))
          : [createEmptyOption(1)];

        const loaded: FormState = {
          name: duplicate
            ? `${entry.name ?? ''} ${tRef.current('common.copySuffix', 'Copy')}`.trim()
            : (entry.name ?? ''),
          sourceType: entry.sourceType ?? 'DB',
          sourceConfig: entry.sourceConfig ?? '',
          queryTemplate: entry.queryTemplate ?? '',
          matchField: entry.matchField ?? '',
          defaultModule: entry.defaultModule ?? '',
          defaultDestination: entry.defaultDestination ?? '',
          description: entry.description ?? '',
          active: Boolean(entry.active ?? true),
          options: adaptedOptions.length > 0 ? adaptedOptions : [createEmptyOption(1)],
        };

        setFormData(loaded);
      } catch (error) {
        showErrorRef.current(tRef.current('dynamicDestination.messages.loadError'));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const loadTargetId = duplicateId || id;
    const savedDataKey = 'dynamicDestinations:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadTargetId || loadTargetId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && loadTargetId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
        
        // Clear sessionStorage for "Save and New" functionality
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Only set default if not in duplicate mode
        if (!duplicateId) {
          // Reset form to default values
          setFormData(getDefaultFormState());
        }
      }
      return;
    }

    if (lastLoadedIdRef.current === loadTargetId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadTargetId;
    lastLocationKeyRef.current = locationKey;
    loadDynamicDestination(loadTargetId, Boolean(duplicateId)).catch(() => {
      lastLoadedIdRef.current = null;
    });
  }, [duplicateId, id, loadDynamicDestination, location.key, location.pathname, location.search]);

  return {
    isLoading,
    formData,
    setFormData
  };
}

